import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Download, ArrowLeft, FileText, CheckCircle, Settings, Trash2, Plus } from 'lucide-react';
import api from '../../services/api';

const PayrollManagement = () => {
    const navigate = useNavigate();
    const today = new Date();
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState([]);
    const [error, setError] = useState(null);
    const [approving, setApproving] = useState(false);

    // Deductions Modal
    const [showDeductions, setShowDeductions] = useState(false);

    const generateReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/payroll/report', { params: { month, year } });
            setReport(res.data);
        } catch (e) {
            setError(e.response?.data?.error || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const approvePayroll = async () => {
        if (!window.confirm('Are you sure you want to approve payroll for this month? This will be visible to employees.')) return;
        setApproving(true);
        try {
            await api.post('/payroll/approve', { month, year });
            alert('Payroll Approved Successfully!');
            generateReport(); // Refresh
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to approve');
        } finally {
            setApproving(false);
        }
    };

    const downloadCsv = () => {
        api.get('/payroll/download-csv', { params: { month, year }, responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `payroll_${year}_${month}.csv`);
                document.body.appendChild(link);
                link.click();
            })
            .catch(() => alert('Failed to download CSV'));
    };

    const viewSlip = (userId) => {
        api.get('/payroll/slip', { params: { userId, month, year }, responseType: 'text' })
            .then(res => {
                const win = window.open('', '_blank');
                win.document.write(res.data);
                win.document.close();
            })
            .catch(e => alert(e.response?.data?.error || 'Failed to get slip'));
    };

    // Rule Management Logic
    const [earningRules, setEarningRules] = useState([]);
    const [deductionRules, setDeductionRules] = useState([]);
    const [newEarningRule, setNewEarningRule] = useState({ name: '', type: 'PERCENTAGE', value: '' });
    const [newDeductionRule, setNewDeductionRule] = useState({ name: '', type: 'FIXED', value: '' });

    const fetchRules = async () => {
        try {
            const [deductions, earnings] = await Promise.all([
                api.get('/payroll/deductions'),
                api.get('/payroll/earnings')
            ]);
            setDeductionRules(deductions.data);
            setEarningRules(earnings.data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSaveRule = async (type) => {
        const isEarning = type === 'EARNING';
        const rule = isEarning ? newEarningRule : newDeductionRule;
        const setRule = isEarning ? setNewEarningRule : setNewDeductionRule;
        const endpoint = isEarning ? '/payroll/earnings' : '/payroll/deductions';
        const defaultState = isEarning ? { name: '', type: 'PERCENTAGE', value: '' } : { name: '', type: 'FIXED', value: '' };

        if (!rule.name || !rule.value) return;
        try {
            if (rule.id) {
                await api.put(`${endpoint}/${rule.id}`, rule);
            } else {
                await api.post(endpoint, rule);
            }
            setRule(defaultState);
            fetchRules();
        } catch (e) {
            alert('Failed to save rule');
        }
    };

    const deleteRule = async (id, type) => {
        if (!confirm('Delete this rule?')) return;
        const endpoint = type === 'EARNING' ? '/payroll/earnings' : '/payroll/deductions';
        try {
            await api.delete(`${endpoint}/${id}`);
            fetchRules();
        } catch (e) {
            alert('Failed');
        }
    };

    const [baseCurrency, setBaseCurrency] = useState('INR');

    const fetchBaseConfig = async () => {
        try {
            const res = await api.get('/admin/config');
            setBaseCurrency(res.data.baseCurrency || 'INR');
        } catch (e) {
            console.error('Failed to fetch config', e);
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: baseCurrency }).format(val);

    useEffect(() => {
        generateReport();
        fetchBaseConfig();
    }, []);

    useEffect(() => {
        if (showDeductions) fetchRules();
    }, [showDeductions]);

    // Validation for Earnings
    const totalEarningPercent = earningRules
        .filter(r => r.type === 'PERCENTAGE')
        .reduce((sum, r) => sum + Number(r.value), 0);

    const isEarningTotalValid = Math.abs(totalEarningPercent - 100) < 0.1;

    const isAllApproved = report.length > 0 && report.every(r => r.status === 'APPROVED');
    const hasData = report.length > 0;

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ display: 'flex', alignItems: 'center', color: '#4b5563', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
                    Back to Home
                </button>
                <button
                    onClick={() => setShowDeductions(!showDeductions)}
                    style={{ display: 'flex', alignItems: 'center', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                >
                    <Settings size={18} style={{ marginRight: '0.5rem' }} />
                    {showDeductions ? 'Hide Configuration' : 'Configure Rules'}
                </button>
            </div>

            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <DollarSign style={{ marginRight: '0.75rem', color: '#2563eb' }} /> Payroll Management
            </h1>

            {showDeductions && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '2rem' }}>
                    {/* Earnings Configuration */}
                    <div className="glass-card" style={{ borderLeft: '4px solid #10b981' }}>
                        <h3>Earnings Rules</h3>
                        {!isEarningTotalValid && (
                            <div style={{ color: 'orange', fontSize: '0.8rem', marginBottom: '10px' }}>
                                Warning: Total Percentage is {totalEarningPercent.toFixed(2)}% (Should be ~100%)
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <input type="text" placeholder="Name" value={newEarningRule.name} onChange={e => setNewEarningRule({ ...newEarningRule, name: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', flex: 1 }} />
                            <select value={newEarningRule.type} onChange={e => setNewEarningRule({ ...newEarningRule, type: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', width: '100px' }}>
                                <option value="PERCENTAGE">% of CTC</option>
                                <option value="FIXED">Fixed</option>
                            </select>
                            <input type="number" placeholder="Value" value={newEarningRule.value} onChange={e => setNewEarningRule({ ...newEarningRule, value: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', width: '70px' }} />
                            <button onClick={() => handleSaveRule('EARNING')} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '0.8rem' }}>
                                {newEarningRule.id ? 'Save' : 'Add'}
                            </button>
                            {newEarningRule.id && <button onClick={() => setNewEarningRule({ name: '', type: 'PERCENTAGE', value: '' })} style={{ padding: '5px', border: 'none', background: '#eee' }}>X</button>}
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {earningRules.map(rule => (
                                <li key={rule.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #eee', alignItems: 'center', fontSize: '0.9rem' }}>
                                    <span style={{ flex: 1 }}><strong>{rule.name}</strong>: {rule.type === 'FIXED' ? `₹${rule.value}` : `${rule.value}%`}</span>
                                    <div>
                                        <button onClick={() => setNewEarningRule(rule)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: '5px' }}>Edit</button>
                                        <button onClick={() => deleteRule(rule.id, 'EARNING')} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Deductions Configuration */}
                    <div className="glass-card" style={{ borderLeft: '4px solid #ef4444' }}>
                        <h3>Deduction Rules</h3>
                        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <input type="text" placeholder="Name" value={newDeductionRule.name} onChange={e => setNewDeductionRule({ ...newDeductionRule, name: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', flex: 1 }} />
                            <select value={newDeductionRule.type} onChange={e => setNewDeductionRule({ ...newDeductionRule, type: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', width: '100px' }}>
                                <option value="FIXED">Fixed</option>
                                <option value="PERCENTAGE">% of Gross</option>
                            </select>
                            <input type="number" placeholder="Value" value={newDeductionRule.value} onChange={e => setNewDeductionRule({ ...newDeductionRule, value: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', width: '70px' }} />
                            <button onClick={() => handleSaveRule('DEDUCTION')} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '0.8rem' }}>
                                {newDeductionRule.id ? 'Save' : 'Add'}
                            </button>
                            {newDeductionRule.id && <button onClick={() => setNewDeductionRule({ name: '', type: 'FIXED', value: '' })} style={{ padding: '5px', border: 'none', background: '#eee' }}>X</button>}
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {deductionRules.map(rule => (
                                <li key={rule.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #eee', alignItems: 'center', fontSize: '0.9rem' }}>
                                    <span style={{ flex: 1 }}><strong>{rule.name}</strong>: {rule.type === 'FIXED' ? `₹${rule.value}` : `${rule.value}%`}</span>
                                    <div>
                                        <button onClick={() => setNewDeductionRule(rule)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: '5px' }}>Edit</button>
                                        <button onClick={() => deleteRule(rule.id, 'DEDUCTION')} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="glass-card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Month</label>
                            <select
                                value={month}
                                onChange={e => setMonth(e.target.value)}
                                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #ddd', minWidth: '150px' }}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Year</label>
                            <input
                                type="number"
                                value={year}
                                onChange={e => setYear(e.target.value)}
                                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #ddd', width: '100px' }}
                            />
                        </div>
                        <button onClick={generateReport} className="btn btn-primary" disabled={loading}>
                            {loading ? 'Generating...' : 'Generate Report'}
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {hasData && (
                            <button
                                onClick={approvePayroll}
                                className="btn"
                                style={{ backgroundColor: isAllApproved ? '#10b981' : '#f59e0b', color: 'white', border: 'none' }}
                                disabled={approving}
                            >
                                {isAllApproved ? <><CheckCircle size={18} style={{ marginRight: '0.5rem' }} /> Approved</> : 'Approve All'}
                            </button>
                        )}
                        <button onClick={downloadCsv} className="btn btn-secondary" disabled={report.length === 0}>
                            <Download size={18} style={{ marginRight: '0.5rem' }} /> Download CSV
                        </button>
                    </div>
                </div>
                {error && <div style={{ color: 'red', marginTop: '1rem' }}>{error}</div>}
            </div>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>ID</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Employee</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Gross Salary</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Paid Days</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Deductions</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Net Pay</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Status</th>
                                <th style={{ padding: '1rem', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.length === 0 ? (
                                <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center' }}>No data available. Click Generate.</td></tr>
                            ) : (
                                report.map((r) => (
                                    <tr key={r.user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#666' }}>{r.user.employeeId || '-'}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontWeight: '500' }}>{r.user.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{r.user.email}</div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>{formatCurrency(r.grossSalary)}</td>
                                        <td style={{ padding: '1rem' }}>{r.payableDays} / {r.daysInMonth}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ color: 'red' }}>-{formatCurrency(r.taxDeductions)}</div>
                                            {(r.unpaidDaysCount > 0) && <div style={{ fontSize: '0.8rem', color: '#b91c1c' }}>({r.unpaidDaysCount} unpaid days)</div>}
                                        </td>
                                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{formatCurrency(r.netPay)}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '0.8rem',
                                                backgroundColor: r.status === 'APPROVED' ? '#d1fae5' : '#fee2e2',
                                                color: r.status === 'APPROVED' ? '#065f46' : '#991b1b'
                                            }}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                                            <button
                                                onClick={() => viewSlip(r.user.id)}
                                                style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem', width: '100%' }}
                                                disabled={r.status !== 'APPROVED'}
                                            // MVP: only view slip if approved? Or allow preview draft? User requested "Historic only approved", admin might want preview. 
                                            // Backend allows Admin Preview. 
                                            >
                                                <FileText size={16} /> Slip
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PayrollManagement;
